// Cell text for the Workflow file preview (UX research W18): what the RM would see in Excel, not what SheetJS stores.
// - Date cells (read with cellDates) and Excel serials in a date column (45352) become "1 Mar 2024".
// - 8-digit yyyymmdd columns (POSN DATE 20260407) become dates too.
// - Floats are rounded: 6 significant digits, or 2 decimals from 1,000 up (27486006.970700003 → 27486006.97).
// Integers and text stay as they are (account numbers must not gain separators).

const DATE_HEADER = /date|\bdt\b|as of|as_of|valuation/i
const DAY = 86_400_000
// Excel's day 0 is 30 Dec 1899 (its 1900 leap-year bug included).
const EXCEL_EPOCH = Date.UTC(1899, 11, 30)

export function formatSheetRows(rows: unknown[][], locale: string): string[][] {
  const head = rows.findIndex((row) => row.some((cell) => String(cell ?? "").trim()))
  const width = Math.max(0, ...rows.map((row) => row.length))
  const kinds = Array.from({ length: width }, (_, column) => columnKind(rows, head, column))
  return rows.map((row, index) => row.map((cell, column) => index === head ? text(cell, locale) : formatCell(cell, kinds[column], locale)))
}

type ColumnKind = "ymd" | "serial" | "plain"

function columnKind(rows: unknown[][], head: number, column: number): ColumnKind {
  if (head < 0) return "plain"
  const values = rows.slice(head + 1).map((row) => row[column]).filter((cell) => String(cell ?? "").trim() !== "")
  if (!values.length) return "plain"
  const dated = DATE_HEADER.test(String(rows[head][column] ?? ""))
  if (values.every((cell) => ymd(cell)) && (dated || values.length >= 3)) return "ymd"
  if (dated && values.every((cell) => cell instanceof Date || serial(cell) !== undefined)) return "serial"
  return "plain"
}

function formatCell(cell: unknown, kind: ColumnKind, locale: string) {
  if (kind === "ymd") {
    const value = ymd(cell)
    if (value) return day(value, locale, true)
  }
  if (kind === "serial" && typeof cell === "number") {
    const value = serial(cell)
    if (value !== undefined) return day(new Date(EXCEL_EPOCH + value * DAY), locale, true)
  }
  return text(cell, locale)
}

function text(cell: unknown, locale: string): string {
  if (cell instanceof Date) return Number.isNaN(cell.getTime()) ? "" : day(sheetJsDate(cell), locale, false)
  if (typeof cell === "number") return number(cell, locale)
  if (typeof cell === "string" && /^-?\d+\.\d{7,}$/.test(cell.trim())) return number(Number(cell), locale)
  return String(cell ?? "")
}

function number(value: number, locale: string) {
  if (!Number.isFinite(value) || Number.isInteger(value)) return String(value)
  return Math.abs(value) >= 1000
    ? formatter(Intl.NumberFormat, locale, "large", { maximumFractionDigits: 2, useGrouping: false }).format(value)
    : formatter(Intl.NumberFormat, locale, "small", { maximumSignificantDigits: 6, useGrouping: false }).format(value)
}

// §50: one Intl formatter per locale and options, not a toLocaleString per cell. The preview now formats every row of
// the sheet (35,151 × 66 cells in Positions), and toLocaleString builds a new formatter on each call: 20 s for a
// sheet like it, against well under 1 s with the formatters kept. The text is the same (toLocaleString is defined as
// this formatter's format).
const formatters = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>()
function formatter<T extends Intl.NumberFormat | Intl.DateTimeFormat>(Kind: new (locale: string, options: object) => T, locale: string, key: string, options: object): T {
  const id = `${Kind.name}|${locale}|${key}`
  const known = formatters.get(id) as T | undefined
  if (known) return known
  const made = new Kind(locale, options)
  formatters.set(id, made)
  return made
}

// SheetJS 0.18.5 (cellDates) builds Dates from its 30 Dec 1899 base in local time but corrects the offset in whole
// minutes, so in a zone whose 1899 offset (LMT) had seconds every Date is off by those seconds: Asia/Qatar 8 s early
// (1 Mar 2024 arrives as 29 Feb 23:59:52), Asia/Riyadh 52 s early, Europe/Madrid 44 s late. Rounding to the minute
// would fix only the first; this adds back exactly the seconds SheetJS dropped (0 in UTC, London, New York). A Date
// already on a whole minute is left alone: it is not off (a workbook time has no seconds the RM would see).
function sheetJsDate(value: Date) {
  if (value.getTime() % 60_000 === 0) return value
  const base = new Date(1899, 11, 30)
  const exact = Date.UTC(1899, 11, 30) - base.getTime()
  const minutes = -base.getTimezoneOffset() * 60_000
  return new Date(value.getTime() + exact - minutes)
}

// A Date read by SheetJS carries the workbook's wall-clock time in local time; a parsed day is read in UTC.
function day(value: Date, locale: string, utc: boolean) {
  const options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric", ...(utc ? { timeZone: "UTC" } : {}) }
  const time = utc ? value.getUTCHours() * 60 + value.getUTCMinutes() : value.getHours() * 60 + value.getMinutes()
  const zone = utc ? "utc" : "local"
  return time
    ? formatter(Intl.DateTimeFormat, locale, `time-${zone}`, { ...options, hour: "numeric", minute: "2-digit" }).format(value)
    : formatter(Intl.DateTimeFormat, locale, `day-${zone}`, options).format(value)
}

function ymd(cell: unknown) {
  const match = /^((?:19|20)\d{2})(\d{2})(\d{2})$/.exec(String(cell ?? "").trim())
  if (!match) return
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
  return date.getUTCMonth() === Number(match[2]) - 1 && date.getUTCDate() === Number(match[3]) ? date : undefined
}

// An Excel day number between 1954 and 2119; fractions carry the time of day.
function serial(cell: unknown) {
  return typeof cell === "number" && cell >= 20_000 && cell < 80_000 ? cell : undefined
}

// "Showing the first N of M rows" (Review 2 §10): a sheet's data-row total, the header row not counted, for a sheet the
// preview cut (more than `limit` rows read); undefined when it is shown whole, so a 150-row sheet formatted down to row
// 5,000 has no limit line. `fullRows` is the full range's row count (SheetJS !fullref), the only count past the rows read.
export function previewDataTotal(read: unknown[][], fullRows?: number, limit = 200) {
  if (read.length <= limit) return undefined
  const filled = read.filter((row) => row.some((cell) => String(cell ?? "").trim())).length
  return Math.max(filled, fullRows ?? 0) - 1
}

// The preview's sheet on the canvas grid (DESIGN-LOG §50): columns lettered as in Excel (A, B, … AA), every row as an
// object keyed by its column index, a column right-aligned when its values read as numbers (the DOM table did this per
// cell with the same pattern; the canvas aligns per column, by most of the filled cells below the header row).
const NUMERIC_CELL = /^-?[\d.,]+%?$/

export function sheetColumnName(index: number): string {
  const letter = String.fromCharCode(65 + (index % 26))
  return index >= 26 ? sheetColumnName(Math.floor(index / 26) - 1) + letter : letter
}

export function sheetGrid(rows: readonly (readonly string[])[], sample = 2000) {
  const width = rows.reduce((widest, row) => Math.max(widest, row.length), 0)
  const step = Math.max(1, Math.floor((rows.length - 1) / sample))
  const columns = Array.from({ length: width }, (_, column) => {
    let filled = 0
    let numeric = 0
    for (let row = 1; row < rows.length; row += step) {
      const value = (rows[row][column] ?? "").trim()
      if (!value) continue
      filled++
      if (NUMERIC_CELL.test(value)) numeric++
    }
    return { key: String(column), label: sheetColumnName(column), align: filled && numeric / filled >= 0.8 ? "right" as const : "left" as const }
  })
  const objects = rows.map((row) => {
    const value: Record<string, string> = {}
    for (let column = 0; column < width; column++) value[column] = row[column] ?? ""
    return value
  })
  return { columns, rows: objects }
}
